import { createSlice, createSelector } from '@reduxjs/toolkit'
import qs from 'qs'

const initialState = {
    cachedQueries: {},
    loading: false,
    currentQuery: null,
    news: {
        cachedQueries: {},
        currentQuery: null,
    },
    subscriptions: {
        cachedQueries: {},
        loading: false,
        currentQuery: null,
    },
    specialOffers: {
        cachedQueries: {},
        currentQuery: null,
    },
    blackFriday: {
        cachedQueries: {},
        loading: false,
        currentQuery: null,
    },
    feedbacks: {
        cachedQueries: {},
        loading: false,
        currentQuery: null,
        filters: [],
    },
}

const pages = createSlice({
    name: 'pages',
    initialState: initialState,
    reducers: {
        requestPage(state) {
            state.loading = true
        },
        successPage(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state

            state.currentQuery = key
            if (!cachedQueries[key]) {
                cachedQueries[key] = { data, error: null }
            }
            state.loading = false
        },
        failurePage(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state

            state.currentQuery = key
            if (!cachedQueries[key]) {
                cachedQueries[key] = { data: null, error: data }
            }
            state.loading = false
        },
        requestNews(state, { payload }) {
            const { key } = payload
            state.news.currentQuery = key
            state.news.cachedQueries[key] = {
                data: null,
                loading: true,
                error: null,
            }
        },
        successNews(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state.news

            state.news.currentQuery = key

            const newData = data || cachedQueries[key]?.data || null
            cachedQueries[key] = { data: newData, error: null, loading: false }
        },
        successOldNews(state, { payload }) {
            const { key } = payload

            state.news.currentQuery = key
        },
        failureNews(state, { payload }) {
            const { key, data } = payload

            state.news.currentQuery = key
            state.news.cachedQueries[key] = { data: null, error: data, loading: false }
        },
        requestFeedbacks(state, { payload }) {
            const { key } = payload
            state.feedbacks.currentQuery = key
            state.feedbacks.cachedQueries[key] = {
                data: null,
                loading: true,
                error: null,
            }
        },
        successFeedbacks(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state.feedbacks

            state.feedbacks.currentQuery = key

            const newData = data || cachedQueries[key]?.data || null
            cachedQueries[key] = { data: newData, error: null, loading: false }

            if (!state.feedbacks.filters.length) {
                state.feedbacks.filters = [...data.filters]
            }
        },
        successOldFeedbacks(state, { payload }) {
            const { key } = payload

            state.feedbacks.currentQuery = key
        },
        failureFeedbacks(state, { payload }) {
            const { key, data } = payload

            state.feedbacks.currentQuery = key
            state.feedbacks.cachedQueries[key] = { data: null, error: data, loading: false }
        },
        requestSubscriptions(state) {
            state.subscriptions.loading = true
        },
        successSubscriptions(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state.subscriptions

            state.subscriptions.currentQuery = key
            if (!cachedQueries[key]) {
                cachedQueries[key] = { data, error: null }
            }
            state.subscriptions.loading = false
        },
        failureSubscriptions(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state.subscriptions

            state.subscriptions.currentQuery = key
            if (!cachedQueries[key]) {
                cachedQueries[key] = { data: null, error: data }
            }
            state.subscriptions.loading = false
        },
        requestSpecialOffers(state, { payload }) {
            const { key } = payload
            state.specialOffers.currentQuery = key
            state.specialOffers.cachedQueries[key] = {
                data: null,
                loading: true,
                error: null,
            }
        },
        successSpecialOffers(state, { payload }) {
            const { key, data } = payload
            const { cachedQueries } = state.specialOffers

            state.specialOffers.currentQuery = key

            const newData = data || cachedQueries[key]?.data || null
            cachedQueries[key] = { data: newData, error: null, loading: false }
        },
        successOldSpecialOffers(state, { payload }) {
            const { key } = payload

            state.specialOffers.currentQuery = key
        },
        failureSpecialOffers(state, { payload }) {
            const { key, data } = payload

            state.specialOffers.currentQuery = key
            state.specialOffers.cachedQueries[key] = { data: null, error: data, loading: false }
        }
    },
})

export const fetchNews = ({ refresh = false, ...params } = {}) => async (dispatch, getState, { api }) => {
    const state = getState().pages.news
    const key = qs.stringify(params)

    try {
        if (!refresh && state.cachedQueries[key]?.data && !state.cachedQueries[key]?.error) {
            dispatch(successOldNews({ key }))

            return
        } else {
            dispatch(requestNews({ key }))
            const response = await api.news(params)
            const result = response?.result ?? 'success'

            if (result === 'success') {
                const { data, meta } = response
                dispatch(successNews({ key, data: { articles: data || [], meta } }))
            } else {
                dispatch(failureNews({ key, data: response }))
            }
        }
    } catch (err) {
        dispatch(failureNews({ key, data: err.toString() }))
    }
}

export const fetchFeedbacks = ({ refresh = false, ...params } = {}) => async (dispatch, getState, { api }) => {
    const state = getState().pages.feedbacks
    const key = qs.stringify(params)

    try {
        if (!refresh && state.cachedQueries[key]?.data && !state.cachedQueries[key]?.error) {
            dispatch(successOldFeedbacks({ key }))

            return
        } else {
            dispatch(requestFeedbacks({ key }))
            const response = await api.feedback(params)
            const result = response?.result ?? 'success'

            if (result === 'success') {
                const { data, meta, filters } = response
                dispatch(successFeedbacks({ key, data: { articles: data || [], meta, filters } }))
            } else {
                dispatch(failureFeedbacks({ key, data: response }))
            }
        }
    } catch (err) {
        dispatch(failureFeedbacks({ key, data: err.toString() }))
    }
}

export const postFeedback = ({ onSucces = null, onError = null, ...params } = {}) => async (dispatch, getState, { api }) => {
    try {
        const response = await api.postFeedback(params)
        const result = response?.result ?? 'success'

        if (result === 'success') {
            onSucces?.()
        } else {
            onError?.(response)
        }

    } catch (err) {
        onError?.(err.toString())
    }
}

export const fetchSubscriptions = params => async (dispatch, getState, { api }) => {
    const state = getState().pages.subscriptions
    const key = qs.stringify(params)

    try {
        if (state.cachedQueries[key]) {
            dispatch(successSubscriptions({ key }))
            return
        } else {
            dispatch(requestSubscriptions())
            const response = await api.subscriptions(params)
            const result = response?.result ?? 'success'
            if (result === 'success') {
                dispatch(successSubscriptions({ key, data: response }))
            } else {
                dispatch(failureSubscriptions({ key, data: response }))
            }
        }
    } catch (err) {
        dispatch(failureSubscriptions({ key, data: err.toString() }))
    }
}

export const fetchSpecialOffers = ({ refresh = false, ...params } = {}) => async (dispatch, getState, { api }) => {
    const state = getState().pages.specialOffers
    const key = qs.stringify(params)

    try {
        if (!refresh && state.cachedQueries[key]?.data && !state.cachedQueries[key]?.error) {
            dispatch(successOldSpecialOffers({ key }))

            return
        } else {
            dispatch(requestSpecialOffers({ key }))
            const response = await api.specialOffers(params)
            const result = response?.result ?? 'success'

            if (result === 'success') {
                const { data, meta } = response

                dispatch(successSpecialOffers({ key, data: { articles: data || [], meta } }))
            } else {
                dispatch(failureSpecialOffers({ key, data: response }))
            }
        }
    } catch (err) {
        dispatch(failureSpecialOffers({ key, data: err.toString() }))
    }
}

export const fetchPageData = ({ slug, type = 'page', access }) => async (
    dispatch,
    getState,
    { api },
) => {
    const state = getState().pages
    const params = { slug: `/${slug}`, type }
    if (access) params.access = access
    const key = qs.stringify(params)

    try {
        if (state.cachedQueries[key]) {
            dispatch(successPage({ key }))
            return
        } else {
            dispatch(requestPage())
            const response = await api.pages(params)
            const { result = 'success', page, articles } = response || {}
            if (result === 'success') {
                dispatch(successPage({ key, data: { ...(page ?? {}), articles: articles || [] } }))
            } else {
                dispatch(failurePage({ key, data: response }))
            }
        }
    } catch (err) {
        dispatch(failurePage({ key, data: err.toString() }))
    }
}

export const fetchMarketProducts = ({ onSuccess = null, onError = null, ids }) => async (dispatch, getState, { api }) => {

    try {
        const response = await api.marketProducts({ ids })
        const result = response?.result ?? 'success'

        if (result === 'success') {
            onSuccess?.(response?.data ?? [])
        } else {
            onError?.()
        }
    } catch (err) {
        onError?.(err)
    }
}


export const getNewsData = createSelector(
    state => state.pages.news,
    news => {
        const { cachedQueries, currentQuery } = news

        return cachedQueries[currentQuery] || {
            data: null,
            loading: false,
            error: null
        }
    },
)

export const getFeedbacksData = createSelector(
    state => state.pages.feedbacks,
    feedbacks => {
        const { cachedQueries, currentQuery, filters } = feedbacks

        return {
            ...cachedQueries[currentQuery],
            filters,
        } || {
            data: null,
            loading: false,
            error: null,
            filters,
        }
    },
)

export const getSubscriptionsData = createSelector(
    state => state.pages.subscriptions,
    subscriptions => {
        const { cachedQueries, currentQuery, loading } = subscriptions
        const cachedQueryData = cachedQueries[currentQuery]?.data
        const cachedQueryError = cachedQueries[currentQuery]?.error

        return {
            data: cachedQueryData,
            loading,
            error: cachedQueryError,
        }
    },
)

export const getSpecialOffersData = createSelector(
    state => state.pages.specialOffers,
    specialOffers => {
        const { cachedQueries, currentQuery } = specialOffers

        return cachedQueries[currentQuery] || {
            data: null,
            loading: false,
            error: null
        }
    },
)

export const {
    requestPage,
    successPage,
    failurePage,
    requestNews,
    successNews,
    successOldNews,
    failureNews,
    requestFeedbacks,
    successFeedbacks,
    successOldFeedbacks,
    failureFeedbacks,
    successSubscriptions,
    requestSubscriptions,
    failureSubscriptions,
    requestSpecialOffers,
    successSpecialOffers,
    successOldSpecialOffers,
    failureSpecialOffers,
} = pages.actions

export default pages.reducer
